import { useState, useEffect } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import classNames from "classnames";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";
import CiroTextInput from "../shared/CiroTextInput";
import CiroDropDown from "../shared/CiroDropdown";
import CiroLink from "../shared/CiroLink";
import CiroCheckbox from "../shared/CiroCheckbox";
import CiroTextArea from "../shared/CiroTextArea";
import CiroModal from "../shared/CiroModal";
import CiroRadioButton from "../shared/CiroRadioButton";
import UserAccountSelector from "../shared/UserAccountSelector";
import { XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";
import BulkAccountSearchFilters from "./BulkAccountSearch/BulkAccountSearchFilters";
import {
  AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
  AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables,
  BulkAccountSearchAdmin_AutopilotStartInternalSessionMutation,
  BulkAccountSearchAdmin_AutopilotStartInternalSessionMutationVariables,
  AutopilotCuratedSearch,
  ScoringCriterion,
  BulkAccountSearchAdmin_AutopilotCuratedSearchesQueryVariables,
  BulkAccountSearchAdmin_AutopilotCuratedSearchesQuery,
  CompanyMatchingSummary,
  BulkAccountSearchAdmin_CreateOrgCompaniesFromCrmAccountsMutation,
  BulkAccountSearchAdmin_CreateOrgCompaniesFromCrmAccountsMutationVariables,
} from "../../__generated__/graphql";
import powersearchRuleValidator from "../autopilotCuratedSearch/CuratedSearchFilters/PowerUserFilters/powersearchRuleValidator";

const BulkAccountSearchFormSchema = yup
  .object({
    searchName: yup.string().required("Search name is required"),
    topContactsPerCompany: yup
      .number()
      .nullable()
      .transform((value: number | null, originalValue: string | null) =>
        originalValue === "" ||
        originalValue === undefined ||
        originalValue === null
          ? undefined
          : value,
      )
      .max(50)
      .when("inputMethod", {
        is: ["report", "orgCompanyIds"],
        then: (schema) =>
          schema.required("Top contacts per company is required"),
        otherwise: (schema) => schema.optional(),
      }),
    selectedCuratedSearch: yup.string().optional(),
    inputMethod: yup
      .string()
      .oneOf(["report", "orgCompanyIds", "none"])
      .required(),
    reportId: yup.string().when("inputMethod", {
      is: "report",
      then: (schema) => schema.required("Report ID is required"),
      otherwise: (schema) => schema.optional(),
    }),
    orgCompanyIds: yup.string().when("inputMethod", {
      is: "orgCompanyIds",
      then: (schema) => schema.required("Company IDs are required"),
      otherwise: (schema) => schema.optional(),
    }),
    selectedUserAccountPk: yup.string().required("User account is required"),
    maxContacts: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .min(1, "Must be at least 1")
      .max(99999, "Must be at most 99999")
      .required("Max contacts is required"),
    maxAccounts: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .min(1, "Must be at least 1")
      .max(500, "Must be at most 500")
      .when("inputMethod", {
        is: "report",
        then: (schema) =>
          schema.required("Max accounts to process is required"),
        otherwise: (schema) => schema.optional(),
      }),
  })
  .required();

export const BulkAccountSearchAdmin_AutopilotCuratedSearches = gql`
  query BulkAccountSearchAdmin_AutopilotCuratedSearches($userAccountPk: ID) {
    autopilotCuratedSearches(
      input: { searchType: INTERNAL, userAccountPk: $userAccountPk }
    ) {
      id
      name
      created_at
      top_contacts_per_company
      internalCuratedSearch {
        id
        opensearchFiltersBlob
        auxiliary_filters {
          companyNames
          orgCompanyIds
        }
      }
      scoringCriteria {
        id
        criterion
        label
        required
        extra_detail
      }
    }
  }
`;

export const BulkAccountSearchAdmin_UpsertAutopilotCuratedSearch = gql`
  mutation BulkAccountSearchAdmin_UpsertAutopilotCuratedSearch(
    $input: UpsertAutopilotCuratedSearchInput!
  ) {
    upsertAutopilotCuratedSearch(input: $input) {
      success
      message
      autopilotCuratedSearch {
        id
      }
    }
  }
`;

export const BulkAccountSearchAdmin_AutopilotStartInternalSession = gql`
  mutation BulkAccountSearchAdmin_AutopilotStartInternalSession(
    $autopilotCuratedSearchId: Int!
    $maxContacts: Int!
    $skipContactsOptions: SkipContactsOptionsInput!
    $userAccountPk: ID
  ) {
    autopilotStartInternalSession(
      autopilotCuratedSearchId: $autopilotCuratedSearchId
      maxContacts: $maxContacts
      skipContactsOptions: $skipContactsOptions
      isCiroEnterprise: true
      userAccountPk: $userAccountPk
    ) {
      success
      message
      session {
        id
      }
    }
  }
`;

export const BulkAccountSearchAdmin_CreateOrgCompaniesFromCRMAccounts = gql`
  mutation BulkAccountSearchAdmin_CreateOrgCompaniesFromCRMAccounts(
    $input: CreateOrgCompaniesFromCRMAccountsInput!
  ) {
    createOrgCompaniesFromCRMAccounts(input: $input) {
      totalProcessed
      successfulMatches {
        accountName
        linkedinSlugs
        matchCount
      }
      failedMatches {
        accountName
        error
      }
      orgCompanyIds
    }
  }
`;

interface BulkAccountSearchFormData {
  searchName: string;
  topContactsPerCompany: number | null;
  selectedCuratedSearch: string;
  inputMethod: "report" | "orgCompanyIds" | "none";
  orgCompanyIds: string;
  selectedUserAccountPk: string;
  reportId: string;
  maxContacts: number;
  maxAccounts: number;
}

interface ScoringCriteria {
  id: string;
  value: string;
  required: boolean;
  extra_detail?: string;
}

const BulkAccountSearchAdmin = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BulkAccountSearchFormData>({
    resolver: yupResolver(BulkAccountSearchFormSchema),
    defaultValues: {
      searchName: "",
      topContactsPerCompany: null,
      selectedCuratedSearch: "",
      inputMethod: "report",
      reportId: "",
      orgCompanyIds: "",
    },
  });

  const [
    selectedCuratedSearch,
    inputMethod,
    reportId,
    selectedUserAccountPk,
    maxContacts,
    maxAccounts,
    orgCompanyIdsString,
  ] = useWatch({
    control,
    name: [
      "selectedCuratedSearch",
      "inputMethod",
      "reportId",
      "selectedUserAccountPk",
      "maxContacts",
      "maxAccounts",
      "orgCompanyIds",
    ],
  });

  const [filtersBlob, setFiltersBlob] = useState<string | null>(null);
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [orgCompanyIds, setOrgCompanyIds] = useState<number[]>([]);
  const [scoringCriteria, setScoringCriteria] = useState<ScoringCriteria[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingScoringCriterion, setEditingScoringCriterion] =
    useState<ScoringCriteria | null>(null);
  const [modalExtraDetail, setModalExtraDetail] = useState("");

  const [filteredCuratedSearches, setFilteredCuratedSearches] = useState<
    AutopilotCuratedSearch[]
  >([]);
  const [companyMatchResults, setCompanyMatchResults] =
    useState<CompanyMatchingSummary | null>(null);
  const [skipContactsYouveAlreadySeen, setSkipContactsYouveAlreadySeen] =
    useState<boolean>(true);

  // Convert orgCompanyIds string to array for filters
  useEffect(() => {
    if (orgCompanyIdsString && inputMethod === "orgCompanyIds") {
      const parsedCompanyIds = orgCompanyIdsString
        .split(/\r?\n/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
      setOrgCompanyIds(parsedCompanyIds);
    } else {
      setOrgCompanyIds([]);
    }
  }, [orgCompanyIdsString, inputMethod]);

  const [getCuratedSearchesData] = useLazyQuery<
    BulkAccountSearchAdmin_AutopilotCuratedSearchesQuery,
    BulkAccountSearchAdmin_AutopilotCuratedSearchesQueryVariables
  >(BulkAccountSearchAdmin_AutopilotCuratedSearches, {
    onCompleted: (data) => {
      const filteredData =
        data?.autopilotCuratedSearches?.filter((search) => {
          return (
            search.internalCuratedSearch &&
            search.internalCuratedSearch.opensearchFiltersBlob &&
            search.internalCuratedSearch.opensearchFiltersBlob.length > 0
          );
        }) || [];
      setFilteredCuratedSearches(filteredData as AutopilotCuratedSearch[]);
    },
  });

  const [createOrgCompaniesFromCRMAccounts, { loading: matchingLoading }] =
    useMutation<
      BulkAccountSearchAdmin_CreateOrgCompaniesFromCrmAccountsMutation,
      BulkAccountSearchAdmin_CreateOrgCompaniesFromCrmAccountsMutationVariables
    >(BulkAccountSearchAdmin_CreateOrgCompaniesFromCRMAccounts, {
      onCompleted: (data) => {
        const matchResults =
          data?.createOrgCompaniesFromCRMAccounts as CompanyMatchingSummary;
        if (matchResults) {
          setCompanyMatchResults(matchResults);
        }
      },
      onError: (error) => {
        console.error("Error matching accounts to companies:", error);
        alert(error.message || "An error occurred while matching companies");
      },
    });

  const curatedSearchDropdownOptions = filteredCuratedSearches.map(
    (search: AutopilotCuratedSearch) => ({
      label: `${search.name} (${search.internalCuratedSearch?.auxiliary_filters?.orgCompanyIds?.length || 0} companies)`,
      value: search.id.toString(),
    }),
  );

  // Pre-populate form when curated search is selected
  const handleCuratedSearchSelection = ({ searchId }: { searchId: string }) => {
    const selectedSearch = filteredCuratedSearches.find(
      (s: AutopilotCuratedSearch) => s.id.toString() === searchId,
    );
    if (selectedSearch) {
      setValue("searchName", selectedSearch.name);
      setValue(
        "topContactsPerCompany",
        selectedSearch.top_contacts_per_company || null,
      );
      setValue(
        "orgCompanyIds",
        selectedSearch.internalCuratedSearch?.auxiliary_filters?.orgCompanyIds?.join(
          "\n",
        ) || "",
      );

      // Pre-populate all filter fields
      setFiltersBlob(
        selectedSearch.internalCuratedSearch?.opensearchFiltersBlob ?? "",
      );
      setCompanyNames(
        selectedSearch.internalCuratedSearch?.auxiliary_filters?.companyNames ||
          [],
      );

      // Pre-populate scoring criteria
      const searchScoringCriteria = selectedSearch.scoringCriteria;
      if (searchScoringCriteria && searchScoringCriteria.length > 0) {
        const populatedCriteria = searchScoringCriteria.map(
          (sc: ScoringCriterion, index: number) => ({
            id: (index + 1).toString(),
            value: sc.criterion,
            required: sc.required || false,
            extra_detail: sc.extra_detail || "",
          }),
        );
        setScoringCriteria(populatedCriteria);
      }
    }
  };

  // Handle company matching and org company creation from CRM report accounts
  const handleCreateCompanies = () => {
    if (reportId && selectedUserAccountPk) {
      createOrgCompaniesFromCRMAccounts({
        variables: {
          input: {
            userAccountPk: selectedUserAccountPk,
            reportId: reportId,
            maxAccounts: maxAccounts || undefined,
          },
        },
      });
    }
  };

  const handleAddScoringCriteria = () => {
    const lastIndexPlusOne = scoringCriteria.length + 1;
    const newId = lastIndexPlusOne.toString();
    setScoringCriteria([
      ...scoringCriteria,
      { id: newId, value: "", required: false, extra_detail: "" },
    ]);
  };

  const handleRemoveScoringCriteria = ({ id }: { id: string }) => {
    setScoringCriteria(scoringCriteria.filter((c) => c.id !== id));
  };

  const handleUpdateScoringCriteria = ({
    id,
    value,
  }: {
    id: string;
    value: string;
  }) => {
    setScoringCriteria(
      scoringCriteria.map((c) => (c.id === id ? { ...c, value } : c)),
    );
  };

  const handleToggleScoringCriteriaRequired = ({
    id,
    required,
  }: {
    id: string;
    required: boolean;
  }) => {
    setScoringCriteria(
      scoringCriteria.map((c) => (c.id === id ? { ...c, required } : c)),
    );
  };

  const handleOpenExtraDetailModal = (criterion: ScoringCriteria) => {
    setEditingScoringCriterion(criterion);
    setModalExtraDetail(criterion.extra_detail || "");
    setModalOpen(true);
  };

  const handleSaveExtraDetail = () => {
    if (editingScoringCriterion) {
      setScoringCriteria(
        scoringCriteria.map((c) =>
          c.id === editingScoringCriterion.id
            ? { ...c, extra_detail: modalExtraDetail }
            : c,
        ),
      );
    }
    setModalOpen(false);
    setEditingScoringCriterion(null);
    setModalExtraDetail("");
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingScoringCriterion(null);
    setModalExtraDetail("");
  };

  const [
    upsertCuratedSearch,
    { data: upsertData, loading: upsertLoading, error: upsertError },
  ] = useMutation<
    AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
    AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables
  >(BulkAccountSearchAdmin_UpsertAutopilotCuratedSearch);

  const [
    startAutopilotSession,
    { data: sessionData, loading: sessionLoading, error: sessionError },
  ] = useMutation<
    BulkAccountSearchAdmin_AutopilotStartInternalSessionMutation,
    BulkAccountSearchAdmin_AutopilotStartInternalSessionMutationVariables
  >(BulkAccountSearchAdmin_AutopilotStartInternalSession);

  const onSubmit = async (data: BulkAccountSearchFormData) => {
    const selectedSearch = filteredCuratedSearches.find(
      (s: AutopilotCuratedSearch) =>
        s.id.toString() === data.selectedCuratedSearch,
    );
    const auxiliaryOrgCompanyIds =
      selectedSearch?.internalCuratedSearch?.auxiliary_filters?.orgCompanyIds ||
      [];
    const hasSavedOrgCompanyIds = auxiliaryOrgCompanyIds.length > 0;

    let companyIdsArray: number[] = [];

    if (["companyIds", "report"].includes(data.inputMethod)) {
      if (!data.topContactsPerCompany) {
        alert("Please enter a value for top contacts per company");
        return;
      }
    }

    if (data.inputMethod === "orgCompanyIds") {
      // Parse company IDs from textarea
      const parsedCompanyIds = data.orgCompanyIds
        .split(/\r?\n/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      if (parsedCompanyIds.length === 0) {
        alert("Please enter at least one company ID");
        return;
      }
      companyIdsArray = parsedCompanyIds.map((id) => parseInt(id));
    } else if (data.inputMethod === "report") {
      // Report ID method - check for existing or matched results
      if (
        !hasSavedOrgCompanyIds &&
        (!data.reportId ||
          !companyMatchResults ||
          companyMatchResults.orgCompanyIds.length === 0)
      ) {
        alert("Please enter a report ID and match companies first");
        return;
      }

      // Use the existing or matched org company IDs
      companyIdsArray = companyMatchResults?.orgCompanyIds?.length
        ? companyMatchResults.orgCompanyIds
        : auxiliaryOrgCompanyIds;

      if (companyIdsArray.length === 0) {
        alert("No companies matched. Please try again.");
        return;
      }
    }

    const validationErrors = powersearchRuleValidator(
      JSON.parse(filtersBlob ?? "{}"),
    );

    if (Object.keys(validationErrors).length > 0) {
      alert(
        `Please fix errors in filters: ${Object.values(validationErrors).join(
          ", ",
        )}`,
      );
      return;
    }

    const isUpdating =
      data.selectedCuratedSearch && data.selectedCuratedSearch !== "";
    const searchId = isUpdating
      ? parseInt(data.selectedCuratedSearch)
      : undefined;

    await upsertCuratedSearch({
      variables: {
        input: {
          id: searchId,
          name: data.searchName,
          promptText: `Bulk search for ${companyIdsArray?.length} companies from ${data.inputMethod === "orgCompanyIds" ? "company IDs list" : "CRM report"}`,
          internalSearchFilters: {
            opensearchFiltersBlob: filtersBlob ?? "",
            auxiliary_filters: {
              companyNames: companyNames?.length > 0 ? companyNames : undefined,
              orgCompanyIds:
                companyIdsArray?.length > 0 ? companyIdsArray : undefined,
            },
          },
          scoringCriteria: scoringCriteria
            .filter((sc) => sc.value.trim())
            .map((sc) => ({
              criterion: sc.value.trim(),
              required: sc.required,
              extra_detail: sc.extra_detail?.trim() || undefined,
            })),
          topContactsPerCompany: data.topContactsPerCompany,
          userAccountPk: data.selectedUserAccountPk,
        },
      },
    });
  };

  const handleRunSession = async () => {
    if (!upsertData?.upsertAutopilotCuratedSearch.autopilotCuratedSearch?.id)
      return;

    await startAutopilotSession({
      variables: {
        autopilotCuratedSearchId:
          upsertData.upsertAutopilotCuratedSearch.autopilotCuratedSearch.id,
        maxContacts: maxContacts ? Number(maxContacts) : 1200,
        skipContactsOptions: {
          skipContactsYouveAlreadySeen: skipContactsYouveAlreadySeen,
          skipContactsFromContactListIds: [],
        },
        userAccountPk: selectedUserAccountPk,
      },
    });
  };

  return (
    <div>
      <div className={classNames("font-medium")}>Ciro Enterprise Search</div>
      <div className={classNames("mb-3", "text-sm", "text-gray-600")}>
        Create and run searches employing enterprise features.
      </div>

      <div className={classNames("grid", "grid-cols-1", "gap-4")}>
        <UserAccountSelector
          value={selectedUserAccountPk}
          onChange={(value) => {
            setValue("selectedUserAccountPk", value || "");
            setValue("selectedCuratedSearch", ""); // Clear selected search when user changes
            setFiltersBlob(""); // Clear filters when user changes
            setCompanyNames([]); // Clear company names when user changes
            setOrgCompanyIds([]); // Clear org company IDs when user changes
            if (value) {
              getCuratedSearchesData({
                variables: { userAccountPk: value },
              });
            }
          }}
          placeholder="Select team member"
          error={errors.selectedUserAccountPk?.message}
        />

        {selectedUserAccountPk && (
          <>
            <CiroDropDown
              placeholder="Select existing search (optional)"
              isClearable
              value={selectedCuratedSearch}
              options={curatedSearchDropdownOptions}
              error={errors.selectedCuratedSearch?.message}
              onChange={(v) => {
                setValue("selectedCuratedSearch", v);
                if (v) {
                  handleCuratedSearchSelection({ searchId: v });
                }
              }}
            />
            <CiroTextInput
              placeholder="New search name"
              register={register("searchName")}
              error={errors.searchName?.message}
            />

            <div className={classNames("space-y-4")}>
              <div
                className={classNames(
                  "text-sm",
                  "font-medium",
                  "text-gray-700",
                )}
              >
                Select Enterprise Company Input Method
              </div>

              <div className={classNames("space-y-3")}>
                <div className={classNames("flex", "gap-6")}>
                  <CiroRadioButton
                    isSelected={inputMethod === "report"}
                    label="Salesforce Report ID"
                    setIsSelected={() => setValue("inputMethod", "report")}
                  />
                  <CiroRadioButton
                    isSelected={inputMethod === "orgCompanyIds"}
                    label="Org Company IDs List"
                    setIsSelected={() =>
                      setValue("inputMethod", "orgCompanyIds")
                    }
                  />
                  <CiroRadioButton
                    isSelected={inputMethod === "none"}
                    label="None"
                    setIsSelected={() => setValue("inputMethod", "none")}
                  />
                </div>

                {inputMethod === "report" && (
                  <div className={classNames("space-y-2")}>
                    <div className={classNames("text-xs", "text-gray-600")}>
                      Enter the Salesforce report ID:
                    </div>
                    <CiroTextInput
                      placeholder="Enter report ID..."
                      register={register("reportId")}
                      error={errors.reportId?.message}
                    />
                  </div>
                )}

                {inputMethod === "orgCompanyIds" && (
                  <div className={classNames("space-y-2")}>
                    <div className={classNames("text-xs", "text-gray-600")}>
                      Paste organization company IDs (one per line):
                    </div>
                    <CiroTextArea
                      placeholder="Enter company IDs, one per line..."
                      register={register("orgCompanyIds")}
                      error={errors.orgCompanyIds?.message}
                      rows={6}
                    />
                  </div>
                )}
              </div>
            </div>

            {inputMethod === "report" && reportId && reportId.trim() && (
              <>
                <CiroTextInput
                  type="number"
                  placeholder="Max accounts to process from report"
                  register={register("maxAccounts")}
                  error={errors.maxAccounts?.message}
                  min="1"
                  max="500"
                />
                <CiroButton
                  style={CiroButtonStyleEnum.PRIMARY}
                  onClick={handleCreateCompanies}
                  disabled={matchingLoading}
                  analyticsField="Match companies from CRM report"
                >
                  {matchingLoading
                    ? "Finding Matches..."
                    : "Find Company Matches"}
                </CiroButton>
              </>
            )}

            {companyMatchResults && (
              <div className={classNames("space-y-3")}>
                <div
                  className={classNames(
                    "text-sm",
                    "text-green-600",
                    "p-3",
                    "bg-green-50",
                    "rounded",
                  )}
                >
                  ✅ Successfully matched{" "}
                  {companyMatchResults.successfulMatches.length} companies
                  {companyMatchResults.failedMatches.length > 0 && (
                    <span className={classNames("text-red-600", "ml-2")}>
                      ({companyMatchResults.failedMatches.length} failed)
                    </span>
                  )}
                </div>

                {companyMatchResults.successfulMatches.length > 0 && (
                  <div
                    className={classNames(
                      "text-xs",
                      "text-gray-600",
                      "max-h-40",
                      "overflow-y-auto",
                      "border",
                      "rounded",
                      "p-2",
                    )}
                  >
                    <div className={classNames("font-medium", "mb-2")}>
                      Successful Matches:
                    </div>
                    {companyMatchResults.successfulMatches.map(
                      (match, index) => (
                        <div
                          key={index}
                          className={classNames(
                            "mb-2",
                            "p-2",
                            "bg-gray-50",
                            "rounded",
                          )}
                        >
                          <div
                            className={classNames(
                              "flex",
                              "items-center",
                              "gap-2",
                              "mb-1",
                            )}
                          >
                            •{" "}
                            <span className={classNames("font-medium")}>
                              {match.accountName}
                            </span>
                            {match.matchCount && (
                              <span
                                className={classNames(
                                  "text-xs",
                                  "text-blue-600",
                                  "bg-blue-100",
                                  "px-2",
                                  "py-1",
                                  "rounded",
                                )}
                              >
                                {match.matchCount} match
                                {match.matchCount > 1 ? "es" : ""}
                              </span>
                            )}
                          </div>
                          {match.linkedinSlugs &&
                            match.linkedinSlugs.length > 0 && (
                              <div
                                className={classNames(
                                  "flex",
                                  "flex-wrap",
                                  "gap-1",
                                  "ml-3",
                                )}
                              >
                                {match.linkedinSlugs.map((slug, slugIndex) => {
                                  const url = `https://www.linkedin.com/company/${slug}`;
                                  return (
                                    <CiroLink key={slugIndex} href={url}>
                                      {slug}
                                    </CiroLink>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                      ),
                    )}
                  </div>
                )}

                {companyMatchResults.failedMatches.length > 0 && (
                  <div
                    className={classNames(
                      "text-xs",
                      "text-red-600",
                      "max-h-40",
                      "overflow-y-auto",
                      "border",
                      "border-red-200",
                      "rounded",
                      "p-2",
                    )}
                  >
                    <div className={classNames("font-medium", "mb-2")}>
                      Failed Matches:
                    </div>
                    {companyMatchResults.failedMatches.map((match, index) => (
                      <div key={index} className={classNames("mb-1")}>
                        • {match.accountName}: {match.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {inputMethod !== "none" && (
          <CiroTextInput
            type="number"
            label="Max qualified contacts per company"
            placeholder="Max qualified contacts per company"
            register={register("topContactsPerCompany")}
            error={errors.topContactsPerCompany?.message}
            max="50"
          />
        )}
        <CiroTextInput
          type="number"
          label="Max qualified contacts for this session"
          placeholder="Max qualified contacts for this session"
          register={register("maxContacts")}
          error={errors.maxContacts?.message}
          min="1"
          max="1200"
        />
        <CiroCheckbox
          checked={skipContactsYouveAlreadySeen}
          onClick={() =>
            setSkipContactsYouveAlreadySeen(!skipContactsYouveAlreadySeen)
          }
          label="Skip contacts you've already seen"
          labelClassName="text-sm"
        />
      </div>

      {/* Filters Section */}
      <div className={classNames("pt-6")}>
        <BulkAccountSearchFilters
          queryBlob={filtersBlob ?? ""}
          companyNames={companyNames}
          orgCompanyIds={orgCompanyIds}
          setSavedFilters={(blob) => {
            setFiltersBlob(blob?.opensearchFiltersBlob ?? "");
            setCompanyNames(blob?.auxiliary_filters?.companyNames || []);
            setOrgCompanyIds(blob?.auxiliary_filters?.orgCompanyIds || []);
          }}
        />
      </div>

      {/* Scoring Criteria Section */}
      <div className={classNames("pt-6")}>
        <h4 className={classNames("text-md", "font-medium", "mb-4")}>
          Scoring criteria
        </h4>
        <div className={classNames("space-y-3")}>
          {scoringCriteria.map((criteria) => (
            <div
              key={criteria.id}
              className={classNames("flex", "items-center", "gap-3")}
            >
              <div className={classNames("flex-1")}>
                <CiroTextInput
                  value={criteria.value}
                  onChange={(e) =>
                    handleUpdateScoringCriteria({
                      id: criteria.id,
                      value: e.target.value,
                    })
                  }
                  placeholder="Enter scoring criterion"
                  wrapperClassName="w-full"
                />
              </div>
              <div className={classNames("flex-shrink-0")}>
                <CiroCheckbox
                  checked={criteria.required}
                  onClick={() =>
                    handleToggleScoringCriteriaRequired({
                      id: criteria.id,
                      required: !criteria.required,
                    })
                  }
                  label="Required"
                  labelClassName="text-sm"
                />
              </div>
              <div className={classNames("flex-shrink-0")}>
                <CiroButton
                  style={CiroButtonStyleEnum.UNSTYLED}
                  onClick={() => handleOpenExtraDetailModal(criteria)}
                  analyticsField="Edit scoring criterion extra detail"
                  title="Add extra detail"
                >
                  <PencilIcon
                    className={classNames("w-5", "h-5", "text-gray-500")}
                  />
                </CiroButton>
              </div>
              <div className={classNames("flex-shrink-0")}>
                <CiroButton
                  style={CiroButtonStyleEnum.UNSTYLED}
                  onClick={() =>
                    handleRemoveScoringCriteria({ id: criteria.id })
                  }
                  analyticsField="Remove scoring criteria"
                >
                  <XMarkIcon
                    className={classNames("w-5", "h-5", "text-gray-500")}
                  />
                </CiroButton>
              </div>
            </div>
          ))}
          <CiroButton
            style={CiroButtonStyleEnum.PRIMARY}
            onClick={handleAddScoringCriteria}
            analyticsField="Add scoring criteria"
          >
            + Add criterion
          </CiroButton>
        </div>
      </div>

      <div className={classNames("pt-6")}>
        <CiroButton
          style={CiroButtonStyleEnum.LOUD}
          onClick={handleSubmit(onSubmit, (errors) => {
            alert("Can't submit due to form errors: " + JSON.stringify(errors));
          })}
          disabled={upsertLoading}
          analyticsField="Create Ciro Enterprise search"
        >
          {upsertLoading
            ? selectedCuratedSearch
              ? "Updating..."
              : "Creating..."
            : selectedCuratedSearch
              ? "Update search"
              : "Create search"}
        </CiroButton>

        {upsertData?.upsertAutopilotCuratedSearch && (
          <div>
            <div>
              Search {selectedCuratedSearch ? "updated" : "created"}:{" "}
              {
                upsertData.upsertAutopilotCuratedSearch.autopilotCuratedSearch
                  ?.id
              }
            </div>
            <CiroButton
              style={CiroButtonStyleEnum.PRIMARY}
              onClick={handleRunSession}
              disabled={sessionLoading}
              analyticsField="Run autopilot session"
            >
              {sessionLoading ? "Starting..." : "Run session"}
            </CiroButton>
          </div>
        )}

        {sessionData?.autopilotStartInternalSession && (
          <div>
            Session started:{" "}
            {sessionData.autopilotStartInternalSession.session?.id}
          </div>
        )}

        {upsertError && <div>Error: {upsertError.message}</div>}
        {sessionError && <div>Error: {sessionError.message}</div>}
      </div>

      <CiroModal isOpen={modalOpen} onClose={handleCloseModal} size="LARGE">
        <h3 className={classNames("text-lg", "font-medium", "mb-4")}>
          Add Extra Detail
        </h3>
        <div className={classNames("mb-4")}>
          <div className={classNames("text-sm", "text-gray-600", "mb-2")}>
            Criterion: {editingScoringCriterion?.value}
          </div>
          <div className={classNames("text-sm", "text-gray-600", "mb-2")}>
            Add additional context or details for this scoring criterion:
          </div>
          <CiroTextArea
            value={modalExtraDetail}
            onChange={(e) => setModalExtraDetail(e.target.value)}
            placeholder="Enter additional details..."
            rows={4}
          />
        </div>
        <div className={classNames("flex", "gap-3", "justify-end")}>
          <CiroButton
            style={CiroButtonStyleEnum.SECONDARY}
            onClick={handleCloseModal}
            analyticsField="Cancel extra detail modal"
          >
            Cancel
          </CiroButton>
          <CiroButton
            style={CiroButtonStyleEnum.PRIMARY}
            onClick={handleSaveExtraDetail}
            analyticsField="Save extra detail"
          >
            Save
          </CiroButton>
        </div>
      </CiroModal>
    </div>
  );
};

export default BulkAccountSearchAdmin;
